/**
 * The onboarding tour's slides, in order. Copy lives in the locale files under
 * `onboarding.slides.<id>` (title, body, imageAlt); screenshots (and drawn
 * illustrations, tests/a11y/onboarding-illustrations.ts) are generated by
 * `npm run onboarding:screenshots` (tests/a11y/onboarding-screenshots.spec.ts)
 * into public/onboarding/<de|en>/<dark|light>/<image>.webp.
 *
 * Tour versions (CLAUDE.md §10): every slide carries the version that added it
 * (`since`). The current tour version is the highest `since` among the slides
 * that are switched on; a user who saw a lower version gets "What's new" once
 * with exactly the slides added since (new accounts get the full tour).
 *
 *   A new slide → `since: <highest version in this list> + 1`. Nothing else.
 *   Several slides of one release share that number.
 *
 * Version 1 is everything before per-slide versions (it counts as seen by
 * every existing user); versions 2 and 3 were whole-tour versions before that.
 */
export interface OnboardingSlide {
  /** Locale key segment: onboarding.slides.<id>.* */
  readonly id: string;
  /** Screenshot base name (NN-slug). */
  readonly image: string;
  /**
   * "Try it now" target. `aquarium` targets live inside an aquarium and are only
   * offered once the user has one; `global` targets always work.
   */
  readonly tryIt?:
    | { readonly scope: 'aquarium'; readonly path: (aquariumId: string) => string }
    | { readonly scope: 'global'; readonly path: string };
  /**
   * Tour version that added the slide. Users who saw an older version get it in
   * "What's new". A new slide gets the highest version in the list + 1.
   */
  readonly since: number;
  /** Position within its release in "What's new" (lower first); the tour order otherwise. */
  readonly whatsNewRank?: number;
  /**
   * Only shown — and only counted towards the tour version — while the feature
   * is switched on (`voice`: VOICE_CAPTURE_ENABLED). If other slides got a higher
   * version before the switch goes on in production, give these slides the next
   * version then, so existing users still get them as new.
   */
  readonly requires?: 'voice';
}

const aq = (sub: string) => ({
  scope: 'aquarium' as const,
  path: (id: string): string => `/aquariums/${encodeURIComponent(id)}${sub}`,
});

export const ONBOARDING_SLIDES: readonly OnboardingSlide[] = [
  { id: 'welcome', image: '01-welcome' , since: 1 },
  // Navigation (3.0): eight areas with tabs, the phone's tab bar and the alerts bell.
  { id: 'navigation', image: '28-navigation' , since: 1 },
  // Health score and "Today" (open Reef Guard findings, overdue tests).
  { id: 'health', image: '02-health-score' , since: 1 },
  { id: 'waterTests', image: '03-water-tests', tryIt: aq('/measurements/new') , since: 1 },
  // Voice journal (Release 2): steps read aloud, timers for the waits, values spoken or typed.
  { id: 'guidedSession', image: '32-guided-session', tryIt: aq('/measurements/session/guided'), since: 4, requires: 'voice', whatsNewRank: 3 },
  { id: 'icp', image: '04-icp-import', tryIt: aq('/icp/upload') , since: 1 },
  // External controller (GHL ProfiLux & co. via Home Assistant): probe values and electricity.
  { id: 'controller', image: '27-controller', tryIt: aq('/integrations/controller') , since: 1 },
  { id: 'history', image: '05-history', tryIt: aq('/measurements?view=history') , since: 1 },
  // Settings → Dashboard: pick up to twelve measurement cards.
  { id: 'dashboardCards', image: '26-dashboard-cards', tryIt: { scope: 'global', path: '/settings#dashboard-cards' } , since: 1 },
  { id: 'reefIntelligence', image: '06-reef-intelligence' , since: 1 },
  // Reef Guard (2.0): swings and critical alerts, targets + test intervals, dose guard.
  { id: 'reefGuard', image: '16-reef-guard', tryIt: { scope: 'global', path: '/alerts' } , since: 1 },
  { id: 'targets', image: '17-targets', tryIt: aq('/parameter-targets') , since: 1 },
  { id: 'doseGuard', image: '18-dose-guard', tryIt: aq('/dosing') , since: 1 },
  { id: 'tasks', image: '07-tasks', tryIt: aq('/tasks') , since: 1 },
  { id: 'livestock', image: '08-livestock', tryIt: aq('/livestock?identify=1') , since: 1 },
  // Reef Guard (2.0): new-tank mode and quarantine.
  { id: 'newTank', image: '19-new-tank' , since: 1 },
  { id: 'quarantine', image: '20-quarantine', tryIt: aq('/quarantine') , since: 1 },
  { id: 'comparison', image: '09-photo-comparison', tryIt: aq('/comparisons') , since: 1 },
  { id: 'gallery', image: '10-gallery', tryIt: aq('/gallery') , since: 1 },
  { id: 'photoAnalysis', image: '11-photo-analysis', tryIt: aq('/coral-check') , since: 1 },
  // Lighting: PAR light map with coral placement, then the AI lighting evaluation.
  { id: 'lightMap', image: '22-light-map', tryIt: aq('/lighting') , since: 1 },
  { id: 'lightEvaluation', image: '23-light-evaluation', tryIt: aq('/lighting') , since: 1 },
  // Flow: pumps, readings as ranges and the flow check per coral, on the same photo as the light map.
  { id: 'flowMap', image: '24-flow-map', tryIt: aq('/flow') , since: 1 },
  { id: 'journal', image: '12-journal', tryIt: aq('/journal') , since: 1 },
  // Voice journal: speak a note at the tank; nothing is saved before "To review".
  { id: 'voiceNotes', image: '30-voice-notes', tryIt: aq('/journal'), since: 4, requires: 'voice', whatsNewRank: 1 },
  { id: 'toReview', image: '31-to-review', tryIt: { scope: 'global', path: '/review' }, since: 4, requires: 'voice', whatsNewRank: 2 },
  { id: 'timeline', image: '13-timeline', tryIt: aq('/timeline') , since: 1 },
  // Reef Guard (2.0): incident timeline and intervention log.
  { id: 'incidents', image: '21-incidents', tryIt: aq('/incidents') , since: 1 },
  // Emergency help: offline first-aid checklist, 7-day values, the mentor with context.
  { id: 'emergency', image: '33-emergency', tryIt: aq('/emergency'), since: 4, requires: 'voice', whatsNewRank: 4 },
  // Equipment safety (3.0): safety checklist and maintenance plans.
  { id: 'equipmentSafety', image: '29-equipment-safety', tryIt: aq('/equipment-safety') , since: 1 },
  // Budget: expenses by category, recurring costs, comparisons and the year-end forecast.
  { id: 'budget', image: '25-budget', tryIt: aq('/budget') , since: 1 },
  { id: 'community', image: '14-community', tryIt: { scope: 'global', path: '/community' } , since: 1 },
  { id: 'start', image: '15-get-started' , since: 1 },
];

/**
 * Title and intro of "What's new" per release: the newest version in it picks
 * `onboarding.whatsNew.releases.<key>`; a release without an entry uses the
 * generic "What's new in ReefGuide".
 */
export const WHATS_NEW_RELEASES: Readonly<Record<number, string>> = {
  4: 'voiceJournal',
};

/** Locale key prefix (`.dialogTitle`, `.intro`) of a "What's new" showing. */
export function whatsNewCopyKey(slides: readonly OnboardingSlide[]): string {
  const newest = Math.max(0, ...slides.map((slide) => slide.since));
  const release = WHATS_NEW_RELEASES[newest];
  return release ? `onboarding.whatsNew.releases.${release}` : 'onboarding.whatsNew.generic';
}

export interface TourFeatures {
  /** Voice notes are switched on (GET /me/onboarding `voice`). */
  readonly voice: boolean;
}

/** The slides of the switched-on features, in tour order. */
export function availableSlides({ voice }: TourFeatures): readonly OnboardingSlide[] {
  return ONBOARDING_SLIDES.filter((slide) => slide.requires !== 'voice' || voice);
}

/** The current tour version: the highest `since` among the switched-on slides. */
export function currentTourVersion(features: TourFeatures): number {
  return Math.max(0, ...availableSlides(features).map((slide) => slide.since));
}

/**
 * The slides of one showing: the full tour, or — "What's new" for a user who saw
 * `whatsNewSince` — only the slides added after it, newest release first (within
 * a release by `whatsNewRank`, then tour order). An empty "What's new" falls
 * back to the full tour.
 */
export function tourSlides(features: TourFeatures, whatsNewSince: number | null = null): readonly OnboardingSlide[] {
  const available = availableSlides(features);
  if (whatsNewSince === null) return available;
  const added = available
    .map((slide, i) => ({ slide, rank: slide.whatsNewRank ?? 1000 + i }))
    .filter(({ slide }) => slide.since > whatsNewSince)
    .sort((a, b) => b.slide.since - a.slide.since || a.rank - b.rank)
    .map(({ slide }) => slide);
  return added.length > 0 ? added : available;
}

/** The "Try it now" path of a slide, or null when it needs an aquarium the user does not have (yet). */
export function tryItPath(slide: OnboardingSlide, aquariumId: string | null): string | null {
  if (!slide.tryIt) return null;
  if (slide.tryIt.scope === 'global') return slide.tryIt.path;
  return aquariumId === null ? null : slide.tryIt.path(aquariumId);
}

/** Intrinsic size of every screenshot (16:10). */
export const SCREENSHOT_WIDTH = 960;
export const SCREENSHOT_HEIGHT = 600;

/** German screenshots for German, English screenshots for every other UI language. */
export function screenshotLocaleGroup(language: string | undefined): 'de' | 'en' {
  return (language ?? '').toLowerCase().startsWith('de') ? 'de' : 'en';
}

export function screenshotUrl(slide: OnboardingSlide, language: string | undefined, theme: 'dark' | 'light'): string {
  const base = import.meta.env.BASE_URL ?? '/';
  return `${base.endsWith('/') ? base : `${base}/`}onboarding/${screenshotLocaleGroup(language)}/${theme}/${slide.image}.webp`;
}
